import { useMemo } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import clsx from 'clsx';

import styles from './QueryPagination.module.css';

/**
 * @param {{
 *   queryParamName?: string;
 *   pagesCount: number;
 *   pagesRange?: number;
 *   page: number;
 *   prevButtonAccessibleLabel?: string;
 *   nextButtonAccessibleLabel?: string;
 * }} props
 * @return {JSX.Element}
 */
export function QueryPagination({
  queryParamName = 'page',
  pagesCount,
  pagesRange = 5,
  page,
  prevButtonAccessibleLabel,
  nextButtonAccessibleLabel,
}) {
  const router = useRouter();

  const template = useMemo(() => {
    const result = [];

    result.push({ label: '1', value: 1 });

    const maxDistance = Math.floor(pagesRange / 2);

    new Array(Math.max(pagesCount - 2, 0)).fill(0).forEach((_, idx) => {
      const currentIdx = idx + 2;

      if (currentIdx < page) {
        const delta = page - currentIdx;

        if (delta <= maxDistance) {
          result.push({ label: String(currentIdx), value: currentIdx });
        } else if (delta === maxDistance + 1) {
          result.push({ label: '...', value: currentIdx });
        }

        return;
      }

      if (currentIdx > page) {
        const delta = currentIdx - page;

        if (delta <= maxDistance) {
          result.push({ label: String(currentIdx), value: currentIdx });
        } else if (delta === maxDistance + 1) {
          result.push({ label: '...', value: currentIdx });
        }

        return;
      }

      result.push({ label: String(currentIdx), value: currentIdx });
    });

    if (pagesCount > 1) {
      result.push({ label: String(pagesCount), value: pagesCount });
    }

    return result;
  }, [page, pagesCount, pagesRange]);

  const disablePrev = page === 1;
  const disableNext = page === pagesCount;

  return (
    <nav className="w-fit">
      <div
        className={clsx('flex flex-row justify-evenly items-center gap-[8px]')}
      >
        <button
          aria-label={prevButtonAccessibleLabel}
          aria-disabled={disablePrev}
          className={clsx(styles.button, disablePrev && styles.buttonDisabled)}
          style={{
            // TODO: Fix this hack! Move these styles into `styles` module.
            //  The issue happens because of a f*cked up global styles
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: disablePrev
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(255, 255, 255, 0.3)',
          }}
          onClick={
            disablePrev
              ? undefined
              : () =>
                  router.push({
                    query: { ...router.query, [queryParamName]: page - 1 },
                  })
          }
        >
          {'<'}
        </button>

        {template.map(({ label, value }) => (
          <Link
            key={value}
            href={{ query: { ...router.query, [queryParamName]: value } }}
            className={clsx(styles.link, page === value && styles.linkActive)}
          >
            {label}
          </Link>
        ))}

        <button
          aria-label={nextButtonAccessibleLabel}
          aria-disabled={disableNext}
          className={clsx(styles.button, disableNext && styles.buttonDisabled)}
          style={{
            // TODO: Fix this hack! Move the these styles into `styles` module.
            //  The issue happens because of a f*cked up global styles
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: disableNext
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(255, 255, 255, 0.3)',
          }}
          onClick={
            disableNext
              ? undefined
              : () =>
                  router.push({
                    query: { ...router.query, [queryParamName]: page + 1 },
                  })
          }
        >
          {'>'}
        </button>
      </div>
    </nav>
  );
}
